import { useParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { useLocation } from '@/api/capacities';
import type { InfoItem } from '@/pages/account/components/InfoGrid';
import { MasterDetailScaffold } from '../shared/master-detail-scaffold';
import { formatWhDateTime } from '../shared/wh-format';
import { activeStatusCell, locationTypeCell, locationTypeLabel } from '../shared/masters-status';
import { LocationRacksTab } from './detail/location-racks-tab';
import { LocationItemsTab } from './detail/location-items-tab';
import { LocationActivityTab } from './detail/location-activity-tab';

export interface CapacityLocationDetailPageProps {
  /** Location id. Falls back to the `:id` route param when omitted. */
  id?: string;
}

/**
 * Location detail — identity header (`#code` + name + type/active) over an info
 * card (code · name · type · address · notes · created/updated). Read-only.
 */
export default function CapacityLocationDetailPage({
  id: idProp,
}: CapacityLocationDetailPageProps) {
  const params = useParams();
  const id = idProp ?? params.id ?? '';

  const { data: location, isLoading, isError, refetch } = useLocation(id);

  const items: InfoItem[] = [
    { label: 'Location Code', value: location?.code },
    { label: 'Location Name', value: location?.name },
    { label: 'Type', value: location ? locationTypeLabel(location.type) : undefined },
    { label: 'Status', value: activeStatusCell(location?.is_active) },
    { label: 'Address', value: location?.address, full: true },
    { label: 'Notes', value: location?.notes, full: true },
    { label: 'Created At', value: formatWhDateTime(location?.created_at) },
    { label: 'Updated At', value: formatWhDateTime(location?.updated_at) },
  ];

  return (
    <MasterDetailScaffold
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      loaded={!!location}
      backTo="/capacities/locations"
      backLabel="Locations"
      codePrefix={location?.code ? `#${location.code}` : undefined}
      title={location?.name ?? ''}
      status={
        <>
          {location ? locationTypeCell(location.type) : null}
          {activeStatusCell(location?.is_active)}
        </>
      }
      cardTitle="Location Information"
      items={items}
    >
      <Tabs defaultValue="racks">
        <TabsList>
          <TabsTrigger value="racks">Racks</TabsTrigger>
          <TabsTrigger value="items">Items at Location</TabsTrigger>
          <TabsTrigger value="activity">Activity Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="racks">
          <LocationRacksTab locationId={id} />
        </TabsContent>
        <TabsContent value="items">
          <LocationItemsTab locationId={id} />
        </TabsContent>
        <TabsContent value="activity">
          <LocationActivityTab id={id} />
        </TabsContent>
      </Tabs>
    </MasterDetailScaffold>
  );
}
